import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin, Crosshair } from "lucide-react";

// Fix default marker icons (webpack/CRA can't resolve Leaflet's relative image paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Default view: center of Indonesia (Java) when no coordinate yet
const DEFAULT_CENTER = [-2.5, 118.0];
const DEFAULT_ZOOM = 5;
const MARKER_ZOOM = 16;

function isValidCoord(lat, lng) {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

// Recenter the map imperatively when target coords change
function Recenter({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (isValidCoord(lat, lng)) {
      map.setView([lat, lng], zoom ?? map.getZoom(), { animate: true });
    }
  }, [lat, lng, zoom, map]);
  // Ensure tiles render correctly after container becomes visible/sized
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

// Place marker on map click
function ClickToPlace({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Recenter/pan the map to follow coordinates typed into the inputs. Skips changes
// that originated from a map interaction (click/drag) so panning isn't hijacked.
function FollowCoord({ lat, lng, skipRef }) {
  const map = useMap();
  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    if (isValidCoord(lat, lng)) {
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }
  }, [lat, lng, map, skipRef]);
  return null;
}

export default function LocationPicker({ lat, lng, onChange, addressParts, hasAddress }) {
  const numLat = lat === "" || lat === null || lat === undefined ? null : Number(lat);
  const numLng = lng === "" || lng === null || lng === undefined ? null : Number(lng);
  const hasMarker = isValidCoord(numLat, numLng);

  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [focus, setFocus] = useState(null); // {lat,lng,zoom} used to drive Recenter
  const skipFollowRef = useRef(false); // true when the last coord change came from the map

  const center = useMemo(() => (hasMarker ? [numLat, numLng] : DEFAULT_CENTER), [hasMarker, numLat, numLng]);
  const initialZoom = hasMarker ? MARKER_ZOOM : DEFAULT_ZOOM;

  // Build a progressive list of geocoding queries from coarsest-detail fallbacks.
  // Indonesian kelurahan (subdistrict) names often don't match OSM's hierarchy,
  // so we try with them first, then drop them progressively.
  const queryChain = useMemo(() => {
    const p = addressParts || {};
    const candidates = [
      [p.address, p.subdistrict, p.district, p.city, p.province, "Indonesia"],
      [p.address, p.district, p.city, p.province, "Indonesia"],
      [p.address, p.city, p.province, "Indonesia"],
      [p.district, p.city, p.province, "Indonesia"],
      [p.city, p.province, "Indonesia"],
    ].map((parts) => parts.filter(Boolean).join(", "));
    return [...new Set(candidates)].filter((q) => q && q.replace(/,|\s|Indonesia/g, "").length > 0);
  }, [addressParts]);

  const pick = (newLat, newLng, zoom) => {
    const rlat = Math.round(newLat * 1e6) / 1e6;
    const rlng = Math.round(newLng * 1e6) / 1e6;
    skipFollowRef.current = true; // change came from the map; don't auto-recenter
    onChange(rlat, rlng);
    setFocus({ lat: rlat, lng: rlng, zoom });
  };

  const geocodeAddress = async () => {
    setGeoError("");
    if (queryChain.length === 0) {
      setGeoError("Lengkapi alamat (provinsi s/d alamat lengkap) terlebih dahulu.");
      return;
    }
    setGeoLoading(true);
    try {
      // Try each query from most specific to coarsest; use the first hit.
      for (const q of queryChain) {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=id&q=${encodeURIComponent(
          q
        )}`;
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        // eslint-disable-next-line no-await-in-loop
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          pick(parseFloat(data[0].lat), parseFloat(data[0].lon), MARKER_ZOOM);
          setGeoLoading(false);
          return;
        }
      }
      setGeoError(
        "Lokasi alamat tidak ditemukan otomatis. Silakan pilih lokasi secara manual dengan menggeser/klik pada peta."
      );
    } catch {
      setGeoError(
        "Gagal mencari lokasi alamat. Silakan pilih lokasi secara manual dengan menggeser/klik pada peta."
      );
    } finally {
      setGeoLoading(false);
    }
  };

  // Auto-geocode once for CREATE mode when address is complete and no marker yet (debounced)
  const [autoTried, setAutoTried] = useState(false);
  useEffect(() => {
    if (hasMarker || !hasAddress || queryChain.length === 0 || autoTried) return;
    const t = setTimeout(() => {
      setAutoTried(true);
      geocodeAddress();
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAddress, queryChain, hasMarker, autoTried]);

  return (
    <div className="space-y-3" data-testid="location-picker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <MapPin size={16} className="text-accent" />
          Geser pin untuk menentukan lokasi properti yang tepat.
        </p>
        <button
          type="button"
          onClick={geocodeAddress}
          disabled={geoLoading}
          data-testid="use-address-location-button"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors disabled:opacity-60"
        >
          {geoLoading ? <Loader2 size={15} className="animate-spin" /> : <Crosshair size={15} />}
          Gunakan Lokasi Alamat
        </button>
      </div>

      {geoError && (
        <p className="text-xs text-destructive" data-testid="location-picker-geo-error">
          {geoError}
        </p>
      )}

      <div
        className="h-64 sm:h-80 w-full overflow-hidden rounded-xl border border-border touch-none"
        data-testid="location-picker-map"
      >
        <MapContainer
          center={center}
          zoom={initialZoom}
          scrollWheelZoom
          touchZoom
          doubleClickZoom
          zoomControl
          dragging
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickToPlace onPick={(la, ln) => pick(la, ln, MARKER_ZOOM)} />
          <Recenter lat={focus?.lat} lng={focus?.lng} zoom={focus?.zoom} />
          <FollowCoord lat={numLat} lng={numLng} skipRef={skipFollowRef} />
          {hasMarker && (
            <Marker
              position={[numLat, numLng]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const p = e.target.getLatLng();
                  pick(p.lat, p.lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      {!hasMarker && (
        <p className="text-xs text-muted-foreground" data-testid="location-picker-hint">
          Belum ada titik lokasi. Klik "Gunakan Lokasi Alamat" atau klik langsung pada peta untuk menempatkan pin.
        </p>
      )}
    </div>
  );
}
